import React, { useState } from 'react';
import {
  Button,
  Input,
  Label,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { apiFetch } from '../lib/apiFetch';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '24px',
    ...shorthands.padding('24px'),
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '380px',
    ...shorthands.padding('20px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow4,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pair: {
    display: 'flex',
    gap: '8px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  previewPane: {
    flex: 1,
    minWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  frame: {
    width: '100%',
    height: '760px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: '8px',
  },
});

// Empty fields fall back to the backend's sample receipt (docs/UI/email-receipt.png).
const SAMPLE = {
  logoUrl: '',
  customerName: 'Jane Díaz',
  amountPaid: '156.10',
  paidDate: '2026-05-30',
  receiptNumber: '2083-8343-0783',
  invoiceNumber: '929UDWDM-0019',
  brand: 'Visa',
  last4: '4498',
  servicePeriod: 'May 30–Jun 30, 2026',
  itemDescription: '20 Yard Dumpster',
  itemQty: '1',
  itemAmount: '140.00',
  taxLabel: 'IVU (11.5%)',
  taxAmount: '16.10',
};

export default function EmailReceiptPreview() {
  const styles = useStyles();
  const [f, setF] = useState(SAMPLE);
  const [recipient, setRecipient] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState<'' | 'preview' | 'send'>('');
  const [msg, setMsg] = useState<{ intent: 'success' | 'error'; text: string } | null>(null);

  const set = (key: keyof typeof SAMPLE) => (_: unknown, data: { value: string }) =>
    setF((prev) => ({ ...prev, [key]: data.value }));

  // Shape the form into the request body the /emails/receipt-* endpoints expect.
  const buildPayload = () => ({
    logoUrl: f.logoUrl,
    customerName: f.customerName,
    amountPaid: f.amountPaid,
    paidDate: f.paidDate,
    receiptNumber: f.receiptNumber,
    invoiceNumber: f.invoiceNumber,
    paymentMethod: { brand: f.brand, last4: f.last4 },
    servicePeriod: f.servicePeriod,
    lineItems: [{ description: f.itemDescription, qty: Number(f.itemQty) || 1, amount: f.itemAmount }],
    taxes: f.taxLabel ? [{ label: f.taxLabel, amount: f.taxAmount }] : [],
  });

  const handlePreview = async () => {
    setBusy('preview');
    setMsg(null);
    try {
      const res = await apiFetch('/emails/receipt-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setPreviewHtml(data.html);
    } catch (e: any) {
      setMsg({ intent: 'error', text: e.message || 'Preview failed' });
    } finally {
      setBusy('');
    }
  };

  const handleSend = async () => {
    setBusy('send');
    setMsg(null);
    try {
      const res = await apiFetch('/emails/receipt-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...buildPayload(), recipient }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Send failed');
      setMsg({ intent: 'success', text: `Receipt sent to ${recipient} (id: ${data.id})` });
    } catch (e: any) {
      setMsg({ intent: 'error', text: e.message || 'Send failed' });
    } finally {
      setBusy('');
    }
  };

  const field = (label: string, key: keyof typeof SAMPLE) => (
    <div className={styles.field}>
      <Label htmlFor={`rcpt-${key}`}>{label}</Label>
      <Input id={`rcpt-${key}`} size="large" value={f[key]} onChange={set(key)} />
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <Text size={500} weight="semibold">Receipt email exerciser</Text>
        <Text className={styles.hint}>
          Renders the payment-receipt template (emails.render_receipt). Preview is
          render-only; Send actually emails via Resend and logs to email_sends.
        </Text>

        <div className={styles.field}>
          <Label htmlFor="rcpt-logoUrl">Logo URL (blank = company logo)</Label>
          <Input id="rcpt-logoUrl" size="large" value={f.logoUrl} onChange={set('logoUrl')}
            placeholder="https://…/logo.png" />
        </div>
        {field('Customer name', 'customerName')}
        {field('Amount paid (USD)', 'amountPaid')}
        {field('Paid date (YYYY-MM-DD)', 'paidDate')}
        {field('Receipt number', 'receiptNumber')}
        {field('Invoice number', 'invoiceNumber')}

        <Text className={styles.sectionLabel}>Payment method</Text>
        <div className={styles.pair}>
          <div className={styles.field} style={{ flex: 1 }}>
            <Label htmlFor="rcpt-brand">Brand</Label>
            <Input id="rcpt-brand" size="large" value={f.brand} onChange={set('brand')} />
          </div>
          <div className={styles.field} style={{ width: '110px' }}>
            <Label htmlFor="rcpt-last4">Last 4</Label>
            <Input id="rcpt-last4" size="large" value={f.last4} onChange={set('last4')} />
          </div>
        </div>

        <Text className={styles.sectionLabel}>Line item</Text>
        {field('Service period', 'servicePeriod')}
        {field('Description', 'itemDescription')}
        <div className={styles.pair}>
          <div className={styles.field} style={{ width: '90px' }}>
            <Label htmlFor="rcpt-itemQty">Qty</Label>
            <Input id="rcpt-itemQty" size="large" value={f.itemQty} onChange={set('itemQty')} />
          </div>
          <div className={styles.field} style={{ flex: 1 }}>
            <Label htmlFor="rcpt-itemAmount">Amount</Label>
            <Input id="rcpt-itemAmount" size="large" value={f.itemAmount} onChange={set('itemAmount')} />
          </div>
        </div>

        <Text className={styles.sectionLabel}>Tax (blank = none)</Text>
        <div className={styles.pair}>
          <div className={styles.field} style={{ flex: 1 }}>
            <Label htmlFor="rcpt-taxLabel">Label</Label>
            <Input id="rcpt-taxLabel" size="large" value={f.taxLabel} onChange={set('taxLabel')} />
          </div>
          <div className={styles.field} style={{ width: '110px' }}>
            <Label htmlFor="rcpt-taxAmount">Amount</Label>
            <Input id="rcpt-taxAmount" size="large" value={f.taxAmount} onChange={set('taxAmount')} />
          </div>
        </div>

        <div className={styles.actions}>
          <Button appearance="primary" onClick={handlePreview} disabled={!!busy}>
            {busy === 'preview' ? <Spinner size="tiny" /> : 'Preview'}
          </Button>
        </div>

        <Text className={styles.sectionLabel}>Send a live test</Text>
        <div className={styles.field}>
          <Label htmlFor="rcpt-recipient">Recipient email</Label>
          <Input
            id="rcpt-recipient"
            size="large"
            type="email"
            value={recipient}
            placeholder="you@example.com"
            onChange={(_, d) => setRecipient(d.value)}
          />
        </div>
        <Button onClick={handleSend} disabled={!!busy || !recipient.trim()}>
          {busy === 'send' ? <Spinner size="tiny" /> : 'Send test email'}
        </Button>

        {msg && (
          <MessageBar intent={msg.intent}>
            <MessageBarBody>{msg.text}</MessageBarBody>
          </MessageBar>
        )}
      </div>

      <div className={styles.previewPane}>
        <Text size={400} weight="semibold">Preview</Text>
        {previewHtml ? (
          <iframe className={styles.frame} title="Receipt preview" srcDoc={previewHtml} />
        ) : (
          <Text className={styles.hint}>Click Preview to render the receipt here.</Text>
        )}
      </div>
    </div>
  );
}
